import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, X } from "@phosphor-icons/react";

export interface GoalResource {
  url: string;
  title: string;
}

interface AddLinkDialogProps {
  /** Controls dialog visibility */
  open: boolean;
  /** Called on Cancel or after a successful add */
  onClose: () => void;
  /** Called with the new link when the user confirms */
  onAdd: (link: GoalResource) => void;
  /** Entity label for the dialog title (default: "goal"). E.g., "goal" → "Add link to goal", "target" → "Add link to target" */
  entityLabel?: string;
}

/**
 * Sub-dialog for adding a resource link (URL + title) to a goal or target.
 * Opened from GoalFormSection2 / TargetFormSection4 via the "Add link" button.
 */
const AddLinkDialog: React.FC<AddLinkDialogProps> = ({
  open,
  onClose,
  onAdd,
  entityLabel = "goal",
}) => {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [urlError, setUrlError] = useState("");

  const handleClose = () => {
    setUrl("");
    setTitle("");
    setUrlError("");
    onClose();
  };

  const handleAdd = () => {
    if (!url.trim()) {
      setUrlError("URL is required");
      return;
    }
    onAdd({ url: url.trim(), title: title.trim() });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 7,
          py: 5,
        }}
      >
        <Link size={20} />
        <Typography component="span" variant="h6" fontWeight={500}>
          Add link to {entityLabel}
        </Typography>
        <IconButton
          size="small"
          aria-label="Close dialog"
          onClick={handleClose}
          sx={{ ml: "auto", color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, py: 0 }}>
        <Stack gap={4} sx={{ py: 3 }}>
          <Stack gap={1}>
            <Typography variant="body2">URL</Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="https://..."
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (urlError) setUrlError("");
              }}
              error={Boolean(urlError)}
              helperText={urlError || undefined}
              aria-label="Resource URL"
              autoFocus
            />
          </Stack>

          <Stack gap={1}>
            <Typography variant="body2">Title</Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="e.g., Teaching video, PDF guide..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-label="Resource title"
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, py: 5, gap: 2 }}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={!url.trim() || !title.trim()}
        >
          Add link
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLinkDialog;
